/** Linko theme scripts: hero gallery, cart drawer, sticky buy bar, video. */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* Hero gallery: one photo whose screen changes on tap, swipe, arrows or dots. */
class LinkoGallery extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('.gallery__slide')];
    this.dots = [...this.querySelectorAll('[data-gallery-dot]')];
    this.index = 0;
    if (this.slides.length < 2) {
      this.querySelectorAll('.gallery__arrow, .gallery__dots, .gallery__hint').forEach((el) => el.remove());
      return;
    }

    const frame = this.querySelector('[data-gallery-frame]');
    let startX = null;
    let swiped = false;

    frame.addEventListener('pointerdown', (e) => {
      startX = e.clientX;
      swiped = false;
    });
    frame.addEventListener('pointerup', (e) => {
      if (startX === null) return;
      const dx = e.clientX - startX;
      startX = null;
      if (Math.abs(dx) > 40) {
        swiped = true;
        this.userGo(this.index + (dx < 0 ? 1 : -1));
      }
    });
    frame.addEventListener('click', (e) => {
      if (swiped || e.target.closest('.gallery__arrow')) return;
      this.userGo(this.index + 1);
    });

    this.querySelector('[data-gallery-prev]').addEventListener('click', () => this.userGo(this.index - 1));
    this.querySelector('[data-gallery-next]').addEventListener('click', () => this.userGo(this.index + 1));
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.userGo(Number(dot.dataset.galleryDot))));

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') this.userGo(this.index + 1);
      if (e.key === 'ArrowLeft') this.userGo(this.index - 1);
    });

    // Show the screens changing by themselves until someone takes over.
    if (!reduceMotion) {
      this.visible = false;
      new IntersectionObserver(([entry]) => (this.visible = entry.isIntersecting)).observe(this);
      this.timer = setInterval(() => {
        if (this.visible && !document.hidden && !this.matches(':hover')) this.go(this.index + 1);
      }, 3500);
    }
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  userGo(index) {
    clearInterval(this.timer);
    this.go(index);
  }

  go(index) {
    const count = this.slides.length;
    this.index = (index + count) % count;
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.index));
    this.dots.forEach((dot, i) => dot.setAttribute('aria-selected', String(i === this.index)));
  }
}
customElements.define('linko-gallery', LinkoGallery);

/* Bundle picker: keep the button price in step with the chosen option. */
document.addEventListener('change', (e) => {
  const input = e.target.closest('.bundle input[name="quantity"]');
  if (!input) return;
  const price = input.form.querySelector('[data-button-price]');
  if (price) price.textContent = input.dataset.label;
});

/* Cart drawer */
const Cart = {
  get drawer() {
    return document.getElementById('CartDrawer');
  },

  open() {
    if (this.drawer && !this.drawer.open) this.drawer.showModal();
  },

  close() {
    if (this.drawer?.open) this.drawer.close();
  },

  async request(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: 'cart-drawer', sections_url: window.location.pathname }),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'Something went wrong.');
    if (data.sections?.['cart-drawer']) this.render(data.sections['cart-drawer']);
    return data;
  },

  render(html) {
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-drawer]');
    const current = document.querySelector('[data-cart-drawer]');
    if (!next || !current) return;
    current.replaceWith(next);
    const count = Number(next.dataset.count);
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  },
};

document.addEventListener('submit', async (e) => {
  const form = e.target;
  if (!form.matches('form[action*="/cart/add"]') || !Cart.drawer) return;
  e.preventDefault();

  const button = form.querySelector('[type="submit"]');
  const error = form.querySelector('[data-form-error]');
  const data = new FormData(form);
  button.setAttribute('aria-busy', 'true');
  if (error) error.hidden = true;

  try {
    await Cart.request(`${window.routes.cart_add_url}.js`, {
      items: [{ id: Number(data.get('id')), quantity: Number(data.get('quantity') || 1) }],
    });
    Cart.open();
  } catch (err) {
    if (error) {
      error.textContent = err.message;
      error.hidden = false;
    }
  } finally {
    button.removeAttribute('aria-busy');
  }
});

// Bots fill in every field, people never see this one: quietly drop those messages.
document.addEventListener('submit', (e) => {
  const trap = e.target.querySelector('[data-honeypot]');
  if (trap && trap.value) e.preventDefault();
});

document.addEventListener('click', async (e) => {
  if (e.target.closest('[data-cart-open]') && Cart.drawer) {
    e.preventDefault();
    Cart.open();
    return;
  }

  if (e.target.closest('[data-cart-close]')) {
    Cart.close();
    return;
  }

  // A click on the dimmed area outside the drawer closes it.
  if (e.target === Cart.drawer) {
    Cart.close();
    return;
  }

  const change = e.target.closest('[data-cart-line]');
  if (change) {
    change.setAttribute('aria-busy', 'true');
    try {
      await Cart.request(`${window.routes.cart_change_url}.js`, {
        line: Number(change.dataset.cartLine),
        quantity: Number(change.dataset.cartQty),
      });
      if (document.querySelector('[data-cart-page]')) window.location.reload();
    } catch (err) {
      change.removeAttribute('aria-busy');
      window.alert(err.message);
    }
  }
});

/* Sticky buy bar on phones: shown once the buy box has scrolled away. */
document.addEventListener('DOMContentLoaded', () => {
  const bar = document.querySelector('[data-sticky-buy]');
  const buy = document.querySelector('.hero .buy');
  if (bar && buy) {
    bar.hidden = false;
    bar.classList.add('is-hidden');
    // Hidden while the buy box, the closing call to action or the footer is on screen.
    const watched = [buy, document.querySelector('.cta'), document.querySelector('.footer')].filter(Boolean);
    const inView = new Set();
    let pastBuy = false;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) inView.add(entry.target);
        else inView.delete(entry.target);
        if (entry.target === buy) pastBuy = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      });
      bar.classList.toggle('is-hidden', !pastBuy || inView.size > 0);
    });
    watched.forEach((el) => observer.observe(el));
  }

  // The hero video plays by itself, unless less motion is asked for; then a tap plays or pauses it.
  document.querySelectorAll('.hero__video video').forEach((video) => {
    video.muted = true;
    video.controls = false;
    if (reduceMotion) video.pause();
    video.addEventListener('click', () => (video.paused ? video.play() : video.pause()));
  });

  // Play the videos only while they are on screen, and always without sound.
  document.querySelectorAll('.tap video').forEach((video) => {
    video.muted = true;
    video.defaultMuted = true;
    video.volume = 0;
    video.controls = false;
    // No controls, so there is no volume button; a tap plays or pauses instead. With less motion asked for,
    // the videos wait for that tap.
    video.addEventListener('click', () => (video.paused ? video.play() : video.pause()));
    video.addEventListener('volumechange', () => {
      if (!video.muted) video.muted = true;
    });
    if (reduceMotion) return;
    new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) video.play().catch(() => {});
        else video.pause();
      },
      { threshold: 0.25 }
    ).observe(video);
  });
});
